import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Portal, Toaster } from '@chakra-ui/react';
import { Alert, Props as AlertProps } from './Alert';
import { getAllAlertToasters, ToastPosition } from './toaster';

export type AlertToastMeta = {
  variant: AlertProps['variant'];
  showIcon?: boolean;
  actions?: AlertProps['actions'];
};

// Chakra's toast group is a stretching flex column, so each toast is aligned to shrink-wrap in its column.
const toastAlignment: Record<ToastPosition, 'center' | 'flex-start' | 'flex-end'> = {
  top: 'center',
  bottom: 'center',
  'top-left': 'flex-start',
  'bottom-left': 'flex-start',
  'top-right': 'flex-end',
  'bottom-right': 'flex-end',
};

// Each mount subscribes to the one module-level toaster store, and dashifi nests several (App's root provider,
// Main's per-portal one, RootLayout), so one renderer is elected and re-elected as they mount and unmount.
const mountOrder: symbol[] = [];
const rerenderCallbacks = new Map<symbol, () => void>();

const notifyAll = () => rerenderCallbacks.forEach(rerender => rerender());

const useIsPrimaryToaster = () => {
  const id = useMemo(() => Symbol('alert-toaster'), []);
  const [, bumpRender] = useState(0);

  useEffect(() => {
    mountOrder.push(id);
    rerenderCallbacks.set(id, () => bumpRender(count => count + 1));
    notifyAll();

    return () => {
      const index = mountOrder.indexOf(id);
      if (index !== -1) {
        mountOrder.splice(index, 1);
      }
      rerenderCallbacks.delete(id);
      notifyAll();
    };
  }, [id]);

  return mountOrder[0] === id;
};

export const AlertToaster: FC = () => {
  const isPrimary = useIsPrimaryToaster();
  const toasters = getAllAlertToasters();

  if (!isPrimary) {
    return null;
  }

  // Portal to <body>: <Toaster> renders its group inline, and inside the app tree (div#root) ancestor churn
  // tears live toasts out of the DOM; toasts are page-level and must live outside the app subtree.
  return (
    <Portal>
      {Array.from(toasters.entries()).map(([position, toaster]) => (
        <Toaster key={position} toaster={toaster}>
          {toast => {
            const meta = (toast.meta || {}) as AlertToastMeta;
            return (
              <Box alignSelf={toastAlignment[position]}>
                <Alert
                  variant={meta.variant || 'success'}
                  title={toast.title as string | undefined}
                  description={toast.description as string | React.JSX.Element}
                  isClosable={toast.closable}
                  showIcon={meta.showIcon}
                  actions={meta.actions}
                  role="none"
                  onClose={() => toaster.dismiss(toast.id)}
                />
              </Box>
            );
          }}
        </Toaster>
      ))}
    </Portal>
  );
};
